/**
 * Zustand store for browser session management
 * Handles multiple concurrent browser containers with state synchronization
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { 
  BrowserSession, 
  SessionStore, 
  ContainerConfig,
  SessionStatus,
  PerformanceMetrics,
  BrowserError
} from '../types/browser.types';

const WORKER_BASE_URL = import.meta.env.VITE_WORKER_URL || 'https://chrome-browser-app.copiablack.workers.dev';
const MAX_SESSIONS = 5;

interface SessionStoreState extends SessionStore {
  // Additional state
  isSpawning: boolean;
  errors: Map<string, BrowserError>;
  metrics: Map<string, PerformanceMetrics>;
  
  // Advanced methods
  updateMetrics: (sessionId: string, metrics: PerformanceMetrics) => void;
  setError: (sessionId: string, error: BrowserError) => void;
  clearError: (sessionId: string) => void;
  getActiveSession: () => BrowserSession | undefined;
  switchSession: (sessionId: string) => void;
  duplicateSession: (sessionId: string) => Promise<BrowserSession>;
  hibernateSession: (sessionId: string) => void;
  restoreSession: (sessionId: string) => void;
}

export const useSessionStore = create<SessionStoreState>()(
  devtools(
    persist(
      (set, get) => ({
        sessions: new Map(),
        activeSessionId: null,
        isSpawning: false,
        errors: new Map(),
        metrics: new Map(),

        createSession: async (config?: Partial<ContainerConfig>) => {
          const state = get();
          
          // Check session limit
          if (state.sessions.size >= MAX_SESSIONS) {
            throw new Error(`Maximum session limit (${MAX_SESSIONS}) reached`);
          }

          set({ isSpawning: true });

          try {
            const sessionId = crypto.randomUUID();
            const requestBody = {
              sessionId,
              config: {
                image: 'neko:latest',
                memory: 512,
                cpu: 1,
                ...config,
                security: {
                  sandbox: true,
                  maxSessionDuration: 3600,
                  idleTimeout: 300,
                  ...config?.security
                }
              }
            };

            // Call Worker to spawn container
            const response = await fetch(`${WORKER_BASE_URL}/spawn`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
              throw new Error(`Failed to spawn container: ${response.statusText}`);
            }

            const data = await response.json();

            const newSession: BrowserSession = {
              id: sessionId,
              containerId: data.containerId,
              workerUrl: WORKER_BASE_URL,
              wsUrl: data.wsUrl,
              status: 'initializing',
              createdAt: new Date(),
              lastActivity: new Date(),
              metadata: {
                browserType: 'chromium',
                resolution: { width: 1920, height: 1080, dpi: 96 },
                capabilities: {
                  audio: true,
                  video: true,
                  clipboard: true,
                  fileTransfer: true,
                  webGL: true,
                  gpu: false
                }
              },
              performance: {
                fps: 0,
                latency: 0,
                bandwidth: 0,
                packetsLost: 0,
                jitter: 0
              }
            };

            set(state => ({
              sessions: new Map(state.sessions).set(sessionId, newSession),
              activeSessionId: sessionId,
              isSpawning: false
            }));

            return newSession;
          } catch (error) {
            set({ isSpawning: false });
            const browserError: BrowserError = {
              code: 'CONTAINER_SPAWN_FAILED',
              message: error instanceof Error ? error.message : 'Unknown error',
              timestamp: new Date(),
              recoverable: true
            };
            throw browserError;
          }
        },

        terminateSession: async (id: string) => {
          const session = get().sessions.get(id);
          if (!session) return;

          try {
            // Call Worker to terminate container
            await fetch(`${WORKER_BASE_URL}/terminate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                sessionId: id, 
                containerId: session.containerId 
              })
            });

            set(state => {
              const newSessions = new Map(state.sessions);
              newSessions.delete(id);
              
              // Clear associated data
              const newErrors = new Map(state.errors);
              newErrors.delete(id);
              const newMetrics = new Map(state.metrics);
              newMetrics.delete(id);
              
              // Update active session if needed
              let newActiveId = state.activeSessionId;
              if (state.activeSessionId === id) {
                const remainingSessions = Array.from(newSessions.keys());
                newActiveId = remainingSessions.length > 0 ? remainingSessions[0] : null;
              }

              return {
                sessions: newSessions,
                errors: newErrors,
                metrics: newMetrics,
                activeSessionId: newActiveId
              };
            });
          } catch (error) {
            console.error('Failed to terminate session:', error);
          }
        },

        updateSession: (id: string, updates: Partial<BrowserSession>) => {
          set(state => {
            const session = state.sessions.get(id);
            if (!session) return state;

            const updatedSession = {
              ...session,
              ...updates,
              lastActivity: new Date()
            };

            return {
              sessions: new Map(state.sessions).set(id, updatedSession)
            };
          });
        },

        getSession: (id: string) => {
          return get().sessions.get(id);
        },

        getAllSessions: () => {
          return Array.from(get().sessions.values());
        },

        setActiveSession: (id: string | null) => {
          if (id && !get().sessions.has(id)) {
            console.warn(`Session ${id} not found`);
            return;
          }
          set({ activeSessionId: id });
        },

        clearSessions: () => {
          const state = get();
          // Terminate all sessions
          state.sessions.forEach(session => {
            state.terminateSession(session.id);
          });
          
          set({
            sessions: new Map(),
            activeSessionId: null,
            errors: new Map(),
            metrics: new Map()
          });
        },

        updateMetrics: (sessionId: string, metrics: PerformanceMetrics) => {
          set(state => ({
            metrics: new Map(state.metrics).set(sessionId, metrics)
          }));
        },

        setError: (sessionId: string, error: BrowserError) => {
          set(state => ({
            errors: new Map(state.errors).set(sessionId, error)
          }));
        },

        clearError: (sessionId: string) => {
          set(state => {
            const newErrors = new Map(state.errors);
            newErrors.delete(sessionId);
            return { errors: newErrors };
          });
        },

        getActiveSession: () => {
          const state = get();
          return state.activeSessionId ? state.sessions.get(state.activeSessionId) : undefined;
        },

        switchSession: (sessionId: string) => {
          const state = get();
          if (!state.sessions.has(sessionId)) {
            console.warn(`Cannot switch to non-existent session: ${sessionId}`);
            return;
          }
          set({ activeSessionId: sessionId });
        },

        duplicateSession: async (sessionId: string) => {
          const session = get().sessions.get(sessionId);
          if (!session) {
            throw new Error(`Session ${sessionId} not found`);
          }

          // Create new session with same config
          return get().createSession({
            memory: 512,
            cpu: 1,
            env: { PARENT_SESSION: sessionId }
          });
        },

        hibernateSession: (sessionId: string) => {
          get().updateSession(sessionId, { status: 'disconnected' });
          // In production, this would save state to Durable Objects
        },

        restoreSession: (sessionId: string) => {
          get().updateSession(sessionId, { status: 'connecting' });
          // In production, this would restore from Durable Objects
        }
      }),
      {
        name: 'browser-sessions',
        partialize: (state) => ({
          // Only persist essential session data
          activeSessionId: state.activeSessionId,
          // Convert Map to array for persistence
          sessions: Array.from(state.sessions.entries()).map(([, session]) => ({
            ...session,
            status: 'disconnected' as SessionStatus // Mark as disconnected on load
          }))
        }),
        onRehydrateStorage: () => (state) => {
          // Reconstruct Maps from persisted arrays
          if (state && Array.isArray((state as any).sessions)) {
            const sessionsArray = (state as any).sessions;
            (state as any).sessions = new Map(
              sessionsArray.map((s: any) => [s.id, s])
            );
          }
        }
      }
    ),
    { name: 'BrowserSessionStore' }
  )
);
